import { fetchMyBuilds, isActiveBuild } from '@/api/builds';
import { socket } from '@/api/socket';
import type { IBuild } from '@/entities/build.entity';
import { $lastBuildComplete } from '@/entities/build-live.entity';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5000;
const POLL_INTERVAL_SOCKET_MS = 30000;

export function useMyBuilds() {
	const [builds, setBuilds] = useState<IBuild[]>([]);
	const [loading, setLoading] = useState(true);
	const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
	const lastBuildComplete = useUnit($lastBuildComplete);

	const loadBuilds = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		try {
			const data = await fetchMyBuilds();
			setBuilds(data);
			return data;
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;

		void (async () => {
			try {
				const data = await fetchMyBuilds();
				if (!cancelled) setBuilds(data);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		const activeBuild = builds.find(isActiveBuild);
		setHasActiveSubscription(Boolean(activeBuild && socket.connected));
	}, [builds]);

	useEffect(() => {
		if (!builds.some(isActiveBuild)) return;

		const intervalMs = hasActiveSubscription && socket.connected ? POLL_INTERVAL_SOCKET_MS : POLL_INTERVAL_MS;
		const intervalId = window.setInterval(() => {
			void loadBuilds();
		}, intervalMs);

		return () => window.clearInterval(intervalId);
	}, [builds, loadBuilds, hasActiveSubscription]);

	useEffect(() => {
		if (!lastBuildComplete) return;
		void loadBuilds();
	}, [lastBuildComplete, loadBuilds]);

	return {
		builds,
		loading,
		loadBuilds,
	};
}
